import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Container, Typography } from 'lib-ui-react';
import useLoan from "hooks/useLoan";
import { FormText } from 'components/UI/form';
import {
  getFirstDueDate,
  getInstallments
} from "utils/helper/loan-helper";
import { showFooter, showMenu } from 'store/actions/util-actions';
import Routes from 'resources/routes.json';
import { goToLoanRoute } from 'navigation/application-routes';
import { isMobile } from 'utils/helper/page-behavior-helper';
import ErrorNotification from 'components/features/error-notification/error-notification';
import SuccessNotification from 'components/features/success-notification/success-notification';
import styles from './styles.module.scss';
import { simulatePayDayChange, underwritePartial, UNDERWRITE_PARTIAL_SUCCESS, UNDERWRITE_PARTIAL_FAILURE, SIMULATE_PAY_DAY_SUCCESS, SIMULATE_PAY_DAY_FAILURE } from '../../../store/actions/loan-actions';

export default function DueDateUpdate() {
  const { loan } = useLoan();
  const [payDay, setPayDay] = useState('');
  const [payDayOffer, setPayDayOffer] = useState(null);
  const [installmentsOffer, setInstallmentsOffer] = useState(null);
  const [originalDueDate] = useState(loan.conditions?.firstInstallment?.dueDate);
  const [validation, setValidation] = useState({
    initialized: false,
    payDay: false
  });
  const [errors, setErrors] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const dispatch = useDispatch();

  const values = [
    {
      label: '1ª parcela',
      value: installmentsOffer || getInstallments(loan)
    },
    {
      label: 'Data de vencimento',
      value: payDayOffer || getFirstDueDate(loan)
    },
  ];

  const footerStyle = isMobile() ? { position: 'fixed', bottom: 0, left: 0 } : {};

  // Show menu and footer
  useEffect(() => {
    dispatch(showMenu(false));
    dispatch(showFooter(false));
  }, [dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (Number(payDay) >= 1 && Number(payDay) <= 30) {
        handleSimulatePayDay(payDay);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [payDay]);

  function updatePayDay(value) {
    if (/^\d{0,2}$/.test(value)) {
      setPayDay(value);
      setValidation({ ...validation, payDay: Number(value) >= 1 && Number(value) <= 30 });
    }
  }

  function handleSimulatePayDay(updatedPayDay) {
    if (loan?.id) {
      setErrors([]);
      dispatch(simulatePayDayChange(loan.id, updatedPayDay)).then((response) => {
        if (response.type === SIMULATE_PAY_DAY_SUCCESS) {
          const responseLoan = structuredClone(loan);
          responseLoan.conditions = response.payload;
          setInstallmentsOffer(getInstallments(responseLoan))
          setPayDayOffer(getFirstDueDate(responseLoan))
          setErrors(null)
        } else if (response.type === SIMULATE_PAY_DAY_FAILURE) {
          setErrors(["Falha ao tentar simular nova data de vencimento, por favor tente novamente."]);
        }
      });
    }
  }

  function handleDueDateEdit() {
    dispatch(underwritePartial(loan.id, payDay, originalDueDate)).then((response) => {
      if (response.type === UNDERWRITE_PARTIAL_SUCCESS) {
        setErrors(null)
        setSuccessMessage("Data de vencimento atualizada.")
        goToLoanRoute(loan, Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION, Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION);
      } else if (response.type === UNDERWRITE_PARTIAL_FAILURE) {
        setSuccessMessage(null);
        setErrors(["Falha ao atualizar a data de vencimento, por favor tente novamente."]);
      }
    });
  }

  function handleCancel(loan) {
    goToLoanRoute(loan, Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION, Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION);
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.centeredContainer}>
        <div className='d-flex justify-content-center'>
          <img src="/image/simple-logo.png" alt="Logo Supersim" width="35"/>
        </div>
        <Container justify="center" align="center">
          <div id="form-area">
            <div className={`${styles.formContainer} mt-4`}>
              <ErrorNotification errors={errors}/>
              <SuccessNotification successMsg={successMessage}/>

              <Typography style={{fontSize: '1.5rem', marginBottom: '10px'}}>
                <span className='text-semibold'>Escolha a data de vencimento</span>
                <span className='text-regular'> e avaliaremos a possibilidade.</span>
              </Typography>
              <Typography size="16px" color="#757575">Informe um número de 1 a 30.</Typography>

              <Container spacing={30} width="100%" style={{marginTop: '36px'}} gap={24}>
                <FormText
                  id="payDay"
                  label={<span className={styles.background}>Dia do pagamento</span>}
                  mandatory
                  formatMask="XX"
                  inputMode="numeric"
                  type="text"
                  style={{
                    backgroundColor: '#fafafa',
                    borderColor: !validation.payDay && payDay ? '#ed2e45' : null
                  }}
                  initialized={validation.initialized}
                  validationPattern="^\d{1,2}$"
                  validationMessage="Insira somente números entre 1 e 30"
                  errorClassName="error"
                  value={payDay}
                  onChange={updatePayDay}
                  autoFocus
                />
              </Container>
              <div className={styles.offerInfo}>
                <div className={styles.leftItem}>
                  <Typography className={styles.itemTitle}>{values[0]?.label}</Typography>
                  <Typography className={styles.itemLabel}>{values[0]?.value}</Typography>
                </div>
                <div className={styles.rightItem}>
                  <Typography className={styles.itemTitle}>{values[1]?.label}</Typography>
                  <Typography className={styles.itemLabel}>{values[1]?.value}</Typography>
                </div>
              </div>
              <Container
                align="center"
                justify="center"
                padding="16px"
                spacing={8}
                background="#FAFAFA"
                width="100%"
                style={footerStyle}
              >
                <Button style={{maxWidth: '360px'}} onClick={() => handleDueDateEdit()}
                        id="confirm-due-date-edit-button">
                  Confirmar
                </Button>
                <Button style={{maxWidth: '360px'}} outlined borderColor="#E0E0E0" onClick={() => handleCancel()}
                        id="cancel-due-date-edit-button">
                  Cancelar
                </Button>
              </Container>
            </div>
          </div>
        </Container>
      </div>
    </div>

      );
      }
